import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Chip, CircularProgress, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button } from '@mui/material';
import { formatMinor, type LoyaltyTier } from '@eprintsmart/shared';
import { listLoyaltyAccounts, type LoyaltyAccountRow } from '../services/loyalty';

/**
 * Every customer's Print Credits balance, richest first.
 *
 * Deliberately shows ₹ balances and tiers, never an earn rate — the rate is
 * internal and lives only on the config page. This board answers "who is
 * holding credits and how much are we on the hook for", which is a liability
 * question, not a marketing one.
 */
const TIER_COLOR: Record<LoyaltyTier, 'default' | 'warning' | 'success'> = {
  SILVER: 'default',
  GOLD: 'warning',
  PLATINUM: 'success',
};

export function CreditBoard() {
  const [rows, setRows] = useState<LoyaltyAccountRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    listLoyaltyAccounts()
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load wallets.'));
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.name, r.email, r.phone, r.uid].filter(Boolean).some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [rows, q]);

  // The total sitting in customer wallets is money already promised away, so
  // it belongs at the top rather than buried under the table.
  const outstanding = useMemo(
    () => (rows ?? []).reduce((sum, r) => sum + (r.balanceMinor ?? 0), 0),
    [rows],
  );

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!filtered) {
    return <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Stack spacing={2}>
      <Button component={RouterLink} to="/loyalty" startIcon={<ArrowBackIcon />} sx={{ alignSelf: 'flex-start' }}>
        Print Credits settings
      </Button>

      <Typography variant="h1">Customer credit board</Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Paper sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Outstanding credits</Typography>
          <Typography variant="h6">{formatMinor(outstanding)}</Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Wallets</Typography>
          <Typography variant="h6">{rows?.length ?? 0}</Typography>
        </Paper>
        <TextField
          size="small"
          label="Search name, email, phone"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 260, ml: 'auto' }}
        />
      </Stack>

      {filtered.length === 0 ? (
        <Alert severity="info">
          {rows?.length
            ? 'No wallet matches that search.'
            : 'No customer has earned Print Credits yet. Wallets appear here after a signed-in customer’s first paid order.'}
        </Alert>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="right">Lifetime earned</TableCell>
                <TableCell align="right">12-month spend</TableCell>
                <TableCell>Last activity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => (
                <TableRow
                  key={r.uid}
                  hover
                  component={RouterLink}
                  to={`/loyalty/customers/${r.uid}`}
                  sx={{ textDecoration: 'none', cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2">{r.name || '(no CRM contact)'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.email || r.phone || r.uid}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.tier} color={TIER_COLOR[r.tier] ?? 'default'} />
                  </TableCell>
                  <TableCell align="right"><strong>{formatMinor(r.balanceMinor ?? 0)}</strong></TableCell>
                  <TableCell align="right">{formatMinor(r.lifetimeEarnedMinor ?? 0)}</TableCell>
                  <TableCell align="right">{formatMinor(r.spend12mMinor ?? 0)}</TableCell>
                  <TableCell>
                    {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString('en-IN') : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
